import React, { useRef, useState, useMemo, useEffect } from 'react';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useAuth } from '../context/AuthContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { apiFetch, ApiError } from '../api/client';
import { getScenarioForMission, loadRuntimeScenario } from '../data/scenarios/index';
import { useMissionMusic } from '../hooks/useMissionMusic';
import { STORY_CHARACTERS, getCharactersForStory } from '../data/storyCharacters';
import { useResponsive } from '../hooks/useResponsive';
import SceneReaderScreen from './SceneReaderScreen';
import MissionChatScreen from './MissionChatScreen';
import TaskDetailScreen from './TaskDetailScreen';
import QuizScreen from './QuizScreen';
import MissionCardsScreen from './MissionCardsScreen';

const CYB = {
  bg:       '#0a0e1a',
  surface:  '#0f1525',
  card:     '#131929',
  border:   'rgba(255,255,255,0.08)',
  borderCy: 'rgba(0,240,255,0.3)',
  cyan:     '#00f0ff',
  orange:   '#ff7a00',
  red:      '#ff2d55',
  green:    '#00e676',
  purple:   '#a855f7',
  textPrim: '#e8eaf6',
  textSec:  '#8892a4',
  textMute: '#4a5568',
};

const TYPE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  scene:    { bg: 'rgba(0,240,255,0.12)',   text: '#00f0ff', label: 'SCENE' },
  chat:     { bg: 'rgba(255,122,0,0.12)',   text: '#ff7a00', label: 'CHAT' },
  task:     { bg: 'rgba(0,230,118,0.12)',   text: '#00e676', label: 'TASK' },
  quiz:     { bg: 'rgba(168,85,247,0.12)',  text: '#a855f7', label: 'QUIZ' },
  card:     { bg: 'rgba(255,122,0,0.12)',   text: '#ff7a00', label: 'CARD' },
  incident: { bg: 'rgba(255,45,85,0.12)',   text: '#ff2d55', label: 'INCIDENT' },
};

interface Objective { id: number; title: string; completed: boolean; }

interface SequenceItem {
  id: number;
  /** Original string id from JSON (e.g. `scene1_chat`) for downstream screen lookup. */
  sourceId?: string;
  type: 'scene' | 'chat' | 'card' | 'quiz' | 'task';
  title: string;
  subtitle?: string;
  description?: string;
  estimatedDuration: string;
  status: 'locked' | 'available' | 'completed';
  image?: any;
  sceneNumber?: number;
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  characterAvatar?: any;
  characters?: string[];
}

interface ChapterGroup {
  id: number;
  title: string;
  itemIndices: number[]; // indices into activeSequence
}

interface ChapterDetail {
  id: number; title: string; subtitle: string; description: string;
  progress: number; estimatedTime: number; isUnlocked: boolean; isCompleted: boolean;
  objectives: Objective[]; sequence: SequenceItem[];
}

interface ChatMessage { from: 'player' | 'char'; text: string; }

const storyKeyMap: Record<number, string> = {
  1: 'egocrisis', 101: 'egocrisis',
  2: 'foundation_gamble',
  102: 'architecture_under_fire',
  201: 'architecture_under_fire',
  3: 'data_storm',
  109: 'cloud_heist', 110: 'neural_override', 111: 'pipeline_inferno',
  202: 'quantum_gambit', 203: 'merge_conflict',
  108: 'arabic_modern', 112: 'arabic_modern', 113: 'arabic_modern',
  114: 'arabic_modern', 115: 'arabic_modern',
};

const MISSION_BANNERS: Record<string, any> = {
  egocrisis:         require('../../assets/images/characters/nina_raoul_working.png'),
  foundation_gamble: require('../../assets/images/missions/foundation_gamble.png'),
  architecture_under_fire: require('../../assets/images/missions/architecture_under_fire.png'),
  cloud_heist:       require('../../assets/images/missions/cloud_heist_banner.png'),
  neural_override:   require('../../assets/images/missions/neural_override_banner.png'),
  pipeline_inferno:  require('../../assets/images/missions/pipeline_inferno_banner.png'),
  data_storm:        require('../../assets/images/missions/data_storm.png'),
};
const FALLBACK_BANNER = require('../../assets/images/missions/architecture_under_fire.png');

const PRIORITY_COLORS: Record<string, string> = {
  Low: '#10B981', Medium: '#3B82F6', High: '#F97316', Critical: '#EF4444',
};

const RELATION_SCORES: Record<string, number> = {
  Nina: 85, Raoul: 70, Youssef: 60, Anna: 45, Emilie: 75, Darius: 50, Samir: 65, default: 55,
};

function relationColor(score: number): string {
  if (score >= 70) return CYB.green;
  if (score >= 40) return CYB.orange;
  return CYB.red;
}

function getCharacterAvatar(storyKey: string, characterName: string): any {
  const chars = STORY_CHARACTERS[storyKey] ?? [];
  const found = chars.find(c => c.name === characterName);
  return found?.avatar ?? null;
}

function SceneAvatarRow({ characters, storyKey }: { characters: string[]; storyKey: string }) {
  return (
    <View style={avS.row}>
      {characters.map((name, idx) => {
        const avatar = getCharacterAvatar(storyKey, name);
        return (
          <View key={name} style={[avS.circle, idx > 0 && avS.overlap]}>
            {avatar
              ? <Image source={avatar} style={avS.img} resizeMode={"cover"} />
              : <View style={avS.fallback}><Text style={avS.init}>{name[0]}</Text></View>}
          </View>
        );
      })}
    </View>
  );
}
const avS = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  circle: { width: 34, height: 34, borderRadius: 17, overflow: 'hidden', borderWidth: 2, borderColor: CYB.bg },
  overlap: { marginLeft: -10 },
  img: { width: 34, height: 34 },
  fallback: { width: 34, height: 34, backgroundColor: '#6366F1', alignItems: 'center', justifyContent: 'center' },
  init: { color: '#fff', fontSize: 12, fontWeight: '700' },
});

export default function ChapterDetailScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();

  const chapterId = route.params?.chapterId ?? 1;
  const missionId = route.params?.missionId ?? 1;

  // Progression DB-driven (items complétés), partagée + réactive.
  const { getCompletedItems, completeItem, loadProgress } = useStoryProgress();
  const itemKey = (it: SequenceItem): string => (it.sourceId ?? String(it.id));
  const completedItems = getCompletedItems(missionId);

  // --- Auth + passerelle de déblocage / chargement du scénario blob -------
  // storyChapterId fourni par ChaptersScreen (ex. "chapter-1"). Connecté : on
  // charge le scénario depuis le BLOB et on vérifie le déblocage (403 -> verrou).
  const { user, getAccessToken } = useAuth();
  const storyChapterId: string | null = route.params?.storyChapterId ?? null;
  const [gate, setGate] = useState<'ok' | 'loading' | 'locked'>(user ? 'loading' : 'ok');
  // Incrémenté après chargement du scénario blob -> force le recalcul de `scenario`.
  const [scenarioTick, setScenarioTick] = useState(0);
  const completePostedRef = useRef(false);

  // Scénario : blob (store runtime) en priorité, repli local ; recalculé au tick.
  const scenario = useMemo(() => getScenarioForMission(missionId), [missionId, scenarioTick]);
  const scenarioChapter = scenario?.chapters?.find((_c: any, i: number) => i === chapterId - 1) ?? scenario?.chapters?.[0];
  const storyKey = storyKeyMap[missionId] ?? 'egocrisis';

  const { isMuted, toggleMute } = useMissionMusic(storyKey);
  const { isDesktop } = useResponsive();

  useEffect(() => {
    const token = getAccessToken();
    if (!user || !token) { setGate('ok'); return; }
    let cancelled = false;
    setGate('loading');
    (async () => {
      // 1) Charge le scénario depuis le BLOB (force refetch -> contenu/déblocage à jour).
      await loadRuntimeScenario(
        missionId,
        () => apiFetch(`/me/stories/${missionId}/scenario`, { token, auth: true }),
        true,
      );
      if (cancelled) return;
      setScenarioTick(t => t + 1);
      // 1b) Charge la progression (items complétés) depuis la base.
      void loadProgress(missionId);
      // 2) Si on vient des Chapitres (storyChapterId), vérifie le déblocage (403 -> verrou).
      if (storyChapterId) {
        try {
          await apiFetch(`/me/stories/${missionId}/chapters/${storyChapterId}`, { token, auth: true });
          if (!cancelled) setGate('ok');
        } catch (e: any) {
          if (!cancelled) setGate(e instanceof ApiError && e.status === 403 ? 'locked' : 'ok');
        }
      } else if (!cancelled) {
        setGate('ok');
      }
    })();
    return () => { cancelled = true; };
  }, [storyChapterId, missionId, user, getAccessToken, loadProgress]);

  const timelineScrollRef = useRef<ScrollView>(null);
  const itemPositions = useRef<number[]>([]);

  const [chatCharacter, setChatCharacter] = useState<{ id: string; name: string; avatar: any; role: string } | null>(null);
  const [chatMessages, setChatMessages] = useState<Record<string, ChatMessage[]>>({});
  const [chatInput, setChatInput] = useState('');

  const [mobileTab, setMobileTab] = useState<'timeline' | 'team' | 'progression'>('timeline');
  const [activeItem, setActiveItem] = useState<{ type: string; params: any } | null>(null);

  let dynSceneCount = 0;
  const buildSequenceFromScenario = (): SequenceItem[] => {
    if (!scenarioChapter) return [];
    const storyChars = STORY_CHARACTERS[storyKey] ?? [];
    return scenarioChapter.sequence.map((item: any, idx: number) => {
      const itemType = item.type === 'story' ? 'scene' : (item.type as SequenceItem['type']);
      const sourceId = typeof item.id === 'string' ? item.id : undefined;
      if (itemType === 'scene') {
        dynSceneCount++;
        return {
          id: idx + 1, sourceId, type: 'scene' as const, title: item.title,
          description: typeof item.content === 'object' && 'narrativeText' in item.content
            ? String(item.content.narrativeText).split('\n\n')[0].replace(/\*\*/g, '') : undefined,
          estimatedDuration: `${item.duration ?? 5} min`,
          status: idx === 0 ? 'available' : 'locked',
          sceneNumber: dynSceneCount,
          characters: storyChars.slice(0, 3).map((c: any) => c.name),
        } as SequenceItem;
      }
      if (itemType === 'chat') return { id: idx + 1, sourceId, type: 'chat' as const, title: item.title, estimatedDuration: `${item.duration ?? 10} min`, status: idx === 0 ? 'available' : 'locked' } as SequenceItem;
      if (itemType === 'card') return { id: idx + 1, sourceId, type: 'card' as const, title: item.title, estimatedDuration: `${item.duration ?? 5} min`, status: 'available' } as SequenceItem;
      if (itemType === 'quiz') return { id: idx + 1, sourceId, type: 'quiz' as const, title: item.title, estimatedDuration: `${item.duration ?? 5} min`, status: idx === 0 ? 'available' : 'locked' } as SequenceItem;
      return { id: idx + 1, sourceId, type: 'task' as const, title: item.title, estimatedDuration: `${item.duration ?? 15} min`, status: idx === 0 ? 'available' : 'locked', priority: 'High' } as SequenceItem;
    });
  };

  const egoCrisisObjectives: Objective[] = [
    { id: 1, title: 'Provisionner des utilisateurs dans Entra ID', completed: true },
    { id: 2, title: 'Configurer les groupes dynamiques', completed: false },
    { id: 3, title: 'Creer un tenant secondaire R&D', completed: false },
    { id: 4, title: 'Appliquer la politique MFA ciblee', completed: false },
  ];

  let sceneCounter = 0;
  const egoCrisisRaw: Omit<SequenceItem, 'sceneNumber' | 'image'>[] = [
    { id: 1, type: 'scene', title: 'Arrivee au studio', description: 'Youssef presente Raoul a l equipe. Premiere mission : securiser les identites cloud.', estimatedDuration: '5 min', status: 'completed', characters: ['Youssef', 'Nina', 'Raoul'] },
    { id: 2, type: 'chat', title: 'Discussion avec Nina', estimatedDuration: '10 min', status: 'completed', characterAvatar: require('../../assets/images/characters/egocrisis_nina.jpg') },
    { id: 3, type: 'card', title: 'Metadonnees et RBAC', estimatedDuration: '8 min', status: 'available' },
    { id: 4, type: 'task', title: 'Creer les utilisateurs', subtitle: 'US-EN-ID-001', estimatedDuration: '15 min', status: 'available', priority: 'High' },
    { id: 5, type: 'scene', title: 'Activation des pouvoirs', description: 'Licences P2 activees. Les fonctionnalites avancees se debloquent.', estimatedDuration: '5 min', status: 'locked', characters: ['Nina', 'Raoul'] },
    { id: 6, type: 'task', title: 'Licences P2', subtitle: 'US-EN-LIC-002', estimatedDuration: '10 min', status: 'locked', priority: 'High' },
    { id: 7, type: 'quiz', title: 'Pourquoi P2?', estimatedDuration: '5 min', status: 'locked' },
    { id: 8, type: 'chat', title: 'Debat groupes', estimatedDuration: '10 min', status: 'locked', characterAvatar: require('../../assets/images/characters/egocrisis_anna.jpg') },
    { id: 9, type: 'task', title: 'Groupes dynamiques', subtitle: 'US-EN-GRP-003', estimatedDuration: '20 min', status: 'locked', priority: 'Medium' },
    { id: 10, type: 'scene', title: 'Le Nexus', description: 'Debat tenant vs resource group. La frontiere physique l emporte.', estimatedDuration: '5 min', status: 'locked', characters: ['Nina', 'Raoul', 'Youssef'] },
    { id: 11, type: 'card', title: 'Isolation par tenant', estimatedDuration: '8 min', status: 'locked' },
    { id: 12, type: 'task', title: 'Creer tenant R&D', subtitle: 'US-EN-TEN-004', estimatedDuration: '25 min', status: 'locked', priority: 'Critical' },
    { id: 13, type: 'scene', title: 'L incident MFA', description: 'Un studio partenaire compromis. Le MFA est non negociable.', estimatedDuration: '5 min', status: 'locked', characters: ['Nina', 'Darius'] },
    { id: 14, type: 'card', title: 'Zero Trust', estimatedDuration: '8 min', status: 'locked' },
    { id: 15, type: 'task', title: 'Configurer MFA', subtitle: 'US-EN-MFA-005', estimatedDuration: '20 min', status: 'locked', priority: 'Critical' },
  ];

  const egoCrisisSequence: SequenceItem[] = egoCrisisRaw.map(item => {
    if (item.type === 'scene') { sceneCounter++; return { ...item, sceneNumber: sceneCounter }; }
    return item as SequenceItem;
  });

  // Contenu de jeu : depuis le scénario (blob via store runtime, sinon local).
  // Repli sur la séquence EgoCrisis bundlée si le scénario est indisponible.
  const builtSequence = buildSequenceFromScenario();
  const activeSequence: SequenceItem[] = builtSequence.length > 0 ? builtSequence : egoCrisisSequence;
  const builtObjectives: Objective[] = scenarioChapter?.learningObjectives?.map((title: string, i: number) => ({ id: i + 1, title, completed: false })) ?? [];
  const activeObjectives: Objective[] = builtObjectives.length > 0 ? builtObjectives : egoCrisisObjectives;

  const getComputedStatus = (item: SequenceItem, index: number): 'locked' | 'available' | 'completed' => {
    if (completedItems.has(itemKey(item))) return 'completed';
    if (item.type === 'card') return 'available';
    if (item.status === 'completed') return 'completed';
    if (item.status === 'available') return 'available';
    if (index === 0) return 'available';
    const prev = activeSequence[index - 1];
    if (getComputedStatus(prev, index - 1) === 'completed') return 'available';
    return 'locked';
  };

  const ch1Done = activeSequence.filter(item => completedItems.has(itemKey(item))).length;
  const ch1Total = activeSequence.length;
  const ch1Progress = ch1Total > 0 ? Math.round((ch1Done / ch1Total) * 100) : 0;

  // Chapitre terminé (100%) -> marque la complétion en BASE (débloque le suivant).
  // Une seule fois par montage ; nécessite storyChapterId + utilisateur connecté.
  useEffect(() => {
    if (!storyChapterId || gate !== 'ok' || completePostedRef.current) return;
    if (ch1Total > 0 && ch1Done >= ch1Total) {
      const token = getAccessToken();
      if (!user || !token) return;
      completePostedRef.current = true;
      apiFetch(`/me/stories/${missionId}/chapters/${storyChapterId}/complete`, {
        method: 'POST', token, auth: true,
      }).catch(() => { completePostedRef.current = false; });
    }
  }, [storyChapterId, gate, ch1Done, ch1Total, missionId, user, getAccessToken]);

  const certLabel = scenario?.certification ?? 'AZ-104';
  const chapterTitle = scenarioChapter?.title ?? (chapterId === 1 ? 'Chapitre I - Gouvernance Entra ID' : 'Chapitre II - Le Reseau');
  const chapterSubtitle = scenarioChapter?.subtitle ?? 'Mise en place d une gouvernance securisee';
  const chapterDesc = missionId === 1 ? 'Nina arrive dans un studio Azure en pleine crise identitaire.' : (scenario?.title ?? 'En cours de developpement.');

  const chapter: ChapterDetail = {
    id: chapterId, title: chapterTitle, subtitle: chapterSubtitle, description: chapterDesc,
    progress: ch1Progress, estimatedTime: 120, isUnlocked: true, isCompleted: ch1Progress === 100,
    objectives: activeObjectives, sequence: activeSequence,
  };

  const storyChars = getCharactersForStory(storyKey);
  const bannerImage = MISSION_BANNERS[storyKey] ?? FALLBACK_BANNER;

  const inlineNavigate = (screenName: string, params: any) => {
    const typeMap: Record<string, string> = {
      SceneReader: 'scene', MissionChat: 'chat', MissionCards: 'card', Quiz: 'quiz', TaskDetail: 'task',
    };
    const type = typeMap[screenName];
    if (type) { setActiveItem({ type, params }); }
    else { navigation.navigate(screenName as never, params as never); }
  };

  const navigateToItem = (item: SequenceItem, computedStatus: 'locked' | 'available' | 'completed') => {
    if (computedStatus === 'locked') return;
    const seq = chapter.sequence;
    const currentIndex = seq.findIndex(s => s.id === item.id);
    const nextItem = currentIndex < seq.length - 1 ? seq[currentIndex + 1] : null;
    const itemIdParam = item.sourceId ?? item.id;
    const nextItemIdParam = nextItem?.sourceId ?? nextItem?.id ?? null;
    const params: Record<string, any> = {
      missionId, chapterId: chapter.id, itemId: itemIdParam, title: item.title,
      nextItemId: nextItemIdParam, nextItemType: nextItem?.type ?? null,
      nextItemTitle: nextItem?.title ?? null, nextItemSceneNumber: nextItem?.sceneNumber ?? null,
      isLastItem: nextItem === null,
    };
    if (isDesktop) {
      switch (item.type) {
        case 'scene': setActiveItem({ type: 'scene', params: { ...params, title: item.title, sceneNumber: item.sceneNumber } }); break;
        case 'chat':  setActiveItem({ type: 'chat', params }); break;
        case 'card':  setActiveItem({ type: 'card', params }); break;
        case 'quiz':  setActiveItem({ type: 'quiz', params }); break;
        case 'task':  setActiveItem({ type: 'task', params: { ...params, title: item.title } }); break;
      }
    } else {
      switch (item.type) {
        case 'scene': navigation.navigate('SceneReader' as never, { ...params, title: item.title, sceneNumber: item.sceneNumber } as never); break;
        case 'chat':  navigation.navigate('MissionChat' as never, params as never); break;
        case 'card':  navigation.navigate('MissionCards' as never, params as never); break;
        case 'quiz':  navigation.navigate('Quiz' as never, params as never); break;
        case 'task':  navigation.navigate('TaskDetail' as never, { ...params, title: item.title } as never); break;
      }
    }
  };

  const markComplete = (item: SequenceItem) => { void completeItem(missionId, itemKey(item)); };

  const handleSendMessage = () => {
    if (!chatCharacter || !chatInput.trim()) return;
    const charId = chatCharacter.id;
    const playerMsg: ChatMessage = { from: 'player', text: chatInput.trim() };
    setChatMessages(prev => ({
      ...prev,
      [charId]: [...(prev[charId] ?? []), playerMsg],
    }));
    setChatInput('');
    setTimeout(() => {
      const reply: ChatMessage = { from: 'char', text: 'Message recu. Je reviens vers toi.' };
      setChatMessages(prev => ({
        ...prev,
        [charId]: [...(prev[charId] ?? []), reply],
      }));
    }, 1000);
  };

  const handleProgressionClick = (index: number) => {
    const item = chapter.sequence[index];
    if (!item) return;
    const computedStatus = getComputedStatus(item, index);
    if (computedStatus === 'locked') return;
    navigateToItem(item, computedStatus);
    if (computedStatus === 'available') {
      markComplete(item);
    }
  };

  const MissionHeader = () => (
    <View style={s.headerWrap}>
      <Image source={bannerImage} style={s.bannerImg} resizeMode={"cover"} />
      <View style={s.bannerOverlay} />
      <View style={s.bannerContent}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.backBtn}>
          <Ionicons name={"arrow-back"} size={20} color={CYB.textPrim} />
        </TouchableOpacity>
        <View style={s.bannerTitles}>
          <View style={s.threatRow}>
            <View style={s.threatBadge}>
              <Text style={s.threatText}>THREAT LEVEL  HIGH</Text>
            </View>
            <TouchableOpacity onPress={toggleMute} style={s.muteBtn} activeOpacity={0.8}>
              <Ionicons
                name={isMuted ? 'volume-mute' : 'volume-high'}
                size={18}
                color={isMuted ? CYB.textMute : CYB.cyan}
              />
            </TouchableOpacity>
          </View>
          <Text style={s.missionTitle}>{chapter.title.toUpperCase()}</Text>
          <Text style={s.missionSub}>{chapter.subtitle}</Text>
          <View style={s.certRow}>
            <View style={s.certBadge}><Text style={s.certText}>{certLabel}</Text></View>
            <Text style={s.stepCount}>{ch1Done}/{ch1Total} etapes</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const TimelineCard = ({ item, index }: { item: SequenceItem; index: number }) => {
    const computedStatus = getComputedStatus(item, index);
    const isLocked = computedStatus === 'locked';
    const isCompleted = computedStatus === 'completed';
    const isAvailable = computedStatus === 'available';
    const tc = TYPE_COLORS[item.type] ?? TYPE_COLORS.task;
    const num = String(index + 1).padStart(2, '0');
    const isActive = isAvailable && !isCompleted;

    const actionLabel = isCompleted ? 'Revoir'
      : item.type === 'scene' ? 'Regarder'
      : item.type === 'chat' ? 'Ouvrir le chat'
      : item.type === 'card' ? 'Lire'
      : 'Commencer';
    const actionColor = isCompleted ? CYB.textSec : tc.text;
    const actionIcon = isCompleted ? 'eye-outline'
      : item.type === 'scene' ? 'play-circle-outline'
      : item.type === 'chat' ? 'chatbox-outline'
      : item.type === 'card' ? 'book-outline'
      : 'arrow-forward';

    return (
      <View
        style={s.timelineRow}
        onLayout={(e) => { itemPositions.current[index] = e.nativeEvent.layout.y; }}
      >
        <View style={s.timelineLeft}>
          <Text style={s.timelineNum}>{num}</Text>
          <View style={[s.timelineDot, {
            backgroundColor: isCompleted ? CYB.green : isAvailable ? tc.text : CYB.textMute,
            shadowColor: isCompleted ? CYB.green : isAvailable ? tc.text : 'transparent',
          }]} />
        </View>
        <View style={[s.card, isActive && { borderColor: tc.text + '55', shadowColor: tc.text }, isLocked && s.cardLocked]}>
          <View style={s.cardTopRow}>
            <View style={[s.typeBadge, { backgroundColor: tc.bg }]}>
              <Text style={[s.typeBadgeText, { color: tc.text }]}>{tc.label}</Text>
            </View>
            <View style={s.durationRow}>
              <Ionicons name={"time-outline"} size={11} color={CYB.textMute} />
              <Text style={s.durationText}>{item.estimatedDuration}</Text>
            </View>
            {isCompleted && (
              <View style={s.statusBadge}>
                <Ionicons name={"checkmark"} size={11} color={CYB.cyan} />
                <Text style={[s.statusText, { color: CYB.cyan }]}>{item.type === 'scene' || item.type === 'chat' ? 'VUE' : 'DONE'} ✓</Text>
              </View>
            )}
            {isLocked && (
              <View style={[s.statusBadge, s.statusLocked]}>
                <Ionicons name={"lock-closed"} size={11} color={CYB.textMute} />
                <Text style={[s.statusText, { color: CYB.textMute }]}>VERROUILLE</Text>
              </View>
            )}
          </View>
          <Text style={[s.cardTitle, isLocked && { color: CYB.textMute }]}>{item.title}</Text>
          {item.description && !isLocked && (
            <Text style={s.cardDesc} numberOfLines={2}>{item.description}</Text>
          )}
          <View style={s.cardMeta}>
            {item.subtitle && <View style={s.codeChip}><Text style={s.codeText}>{item.subtitle}</Text></View>}
            {item.priority && (
              <View style={[s.priorityChip, { backgroundColor: PRIORITY_COLORS[item.priority] + '22' }]}>
                <Text style={[s.priorityText, { color: PRIORITY_COLORS[item.priority] }]}>{item.priority}</Text>
              </View>
            )}
            {item.characters && item.characters.length > 0 && !isLocked && (
              <View style={s.avatarsRight}>
                <SceneAvatarRow characters={item.characters} storyKey={storyKey} />
              </View>
            )}
            {item.characterAvatar && !isLocked && (
              <Image source={item.characterAvatar} style={s.charAvatarSm} resizeMode={"cover"} />
            )}
          </View>
          {isLocked && (
            <View style={s.lockOverlay}>
              <Ionicons name={"lock-closed"} size={22} color={CYB.textMute} />
              <Text style={s.lockText}>Requiert: completion de l etape precedente</Text>
            </View>
          )}
          {!isLocked && (
            <TouchableOpacity
              style={[s.actionBtn, { borderColor: actionColor + '88', backgroundColor: isCompleted ? 'transparent' : tc.bg }]}
              onPress={() => { navigateToItem(item, computedStatus); if (isAvailable) markComplete(item); }}
              activeOpacity={0.8}
            >
              <Text style={[s.actionBtnText, { color: actionColor }]}>{actionLabel}</Text>
              <Ionicons name={actionIcon as any} size={13} color={actionColor} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const TimelineColumn = () => (
    <View style={s.timelineWrap}>
      <View style={s.verticalLine} />
      {chapter.sequence.map((item, index) => (
        <TimelineCard key={item.id} item={item} index={index} />
      ))}
    </View>
  );

  // Chapter groups for accordion progression
  const chapterGroups: ChapterGroup[] = useMemo(() => {
    const seq = chapter.sequence;
    if (seq.length === 0) return [];
    // Group by scene breaks: each SCENE starts a new chapter group
    const groups: ChapterGroup[] = [];
    let current: ChapterGroup | null = null;
    seq.forEach((item, idx) => {
      if (item.type === 'scene' || current === null) {
        if (current) groups.push(current);
        current = {
          id: groups.length + 1,
          title: item.type === 'scene' ? item.title : `Phase ${groups.length + 1}`,
          itemIndices: item.type === 'scene' ? [idx] : [idx],
        };
      } else {
        current.itemIndices.push(idx);
      }
    });
    if (current) groups.push(current);
    return groups;
  }, [chapter.sequence]);

  const [expandedChapters, setExpandedChapters] = React.useState<Record<number, boolean>>({ 1: true });
  const toggleChapter = (id: number) => setExpandedChapters(prev => ({ ...prev, [id]: !prev[id] }));

  const isChapterUnlocked = (group: ChapterGroup): boolean => {
    // First chapter always unlocked
    if (group.id === 1) return true;
    // Chapter unlocked if previous chapter has at least one completed item
    const prevGroup = chapterGroups[group.id - 2];
    if (!prevGroup) return true;
    return prevGroup.itemIndices.some(idx => getComputedStatus(chapter.sequence[idx], idx) === 'completed');
  };

  const getChapterProgress = (group: ChapterGroup): number => {
    const done = group.itemIndices.filter(idx => getComputedStatus(chapter.sequence[idx], idx) === 'completed').length;
    return group.itemIndices.length > 0 ? Math.round((done / group.itemIndices.length) * 100) : 0;
  };

  const ProgressionPanel = () => (
    <View style={s.panelWrap}>
      <Text style={s.panelTitle}>PROGRESSION</Text>
      <View style={s.progTrack}><View style={[s.progFill, { width: `${ch1Progress}%` as any }]} /></View>
      <Text style={[s.progSceneLabel, { marginTop: 6 }]}>{ch1Done}/{ch1Total} etapes</Text>
      <View style={{ marginTop: 10, gap: 4 }}>
        {chapterGroups.map(group => {
          const unlocked = isChapterUnlocked(group);
          const expanded = expandedChapters[group.id] ?? false;
          const pct = getChapterProgress(group);
          const allDone = pct === 100;
          return (
            <View key={group.id} style={s.chapterAccordion}>
              <TouchableOpacity
                style={[s.chapterHeader, !unlocked && s.chapterHeaderLocked]}
                onPress={() => unlocked && toggleChapter(group.id)}
                activeOpacity={unlocked ? 0.7 : 1}
              >
                <View style={[s.chapterNum, { backgroundColor: allDone ? CYB.green + '22' : unlocked ? 'rgba(0,240,255,0.1)' : 'rgba(255,255,255,0.04)' }]}>
                  {unlocked
                    ? <Text style={[s.chapterNumText, { color: allDone ? CYB.green : CYB.cyan }]}>{String(group.id).padStart(2,'0')}</Text>
                    : <Ionicons name={"lock-closed"} size={10} color={CYB.textMute} />}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[s.chapterTitle, !unlocked && { color: CYB.textMute }]} numberOfLines={1}>{group.title}</Text>
                  <View style={s.chapterProgRow}>
                    <View style={s.chapterProgTrack}>
                      <View style={[s.chapterProgFill, { width: `${pct}%` as any, backgroundColor: allDone ? CYB.green : CYB.cyan }]} />
                    </View>
                    <Text style={[s.chapterProgPct, { color: allDone ? CYB.green : CYB.textMute }]}>{pct}%</Text>
                  </View>
                </View>
                {unlocked && (
                  <Ionicons name={expanded ? "chevron-up" : "chevron-down"} size={12} color={CYB.textMute} />
                )}
              </TouchableOpacity>
              {unlocked && expanded && (
                <View style={s.chapterItems}>
                  {group.itemIndices.map(idx => {
                    const item = chapter.sequence[idx];
                    const st = getComputedStatus(item, idx);
                    const dotColor = st === 'completed' ? CYB.green : st === 'available' ? CYB.orange : CYB.textMute;
                    return (
                      <TouchableOpacity
                        key={item.id}
                        style={[s.progItem, { paddingLeft: 8, paddingVertical: 4 }]}
                        onPress={() => handleProgressionClick(idx)}
                        activeOpacity={0.7}
                      >
                        <View style={[s.progDot, { backgroundColor: dotColor }]} />
                        <Text style={[s.progItemText, { color: st === 'locked' ? CYB.textMute : CYB.textSec }]} numberOfLines={1}>
                          {String(idx + 1).padStart(2, '0')}  {item.title}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );

  const TeamPanel = () => (
    <View style={s.panelWrap}>
      <Text style={s.panelTitle}>TEAM</Text>
      {storyChars.slice(0, 6).map(char => {
        const score = RELATION_SCORES[char.name] ?? 55;
        const barColor = relationColor(score);
        return (
          <View key={char.name} style={s.teamRow}>
            {char.avatar
              ? <Image source={char.avatar} style={s.teamAvatar} resizeMode={"cover"} />
              : <View style={[s.teamAvatar, s.teamAvatarFb]}><Text style={s.teamAvatarInit}>{char.name[0]}</Text></View>}
            <View style={s.teamInfo}>
              <Text style={s.teamName}>{char.name}</Text>
              <Text style={s.teamRole}>{char.role}</Text>
              <View style={s.relationRow}>
                <View style={s.relationTrack}>
                  <View style={[s.relationFill, { width: `${score}%` as any, backgroundColor: barColor }]} />
                </View>
                <Text style={[s.relationScore, { color: barColor }]}>{score}/100</Text>
              </View>
            </View>
            <TouchableOpacity
              style={s.chatIconBtn}
              onPress={() => setChatCharacter({ id: char.name, name: char.name, avatar: char.avatar, role: char.role })}
              activeOpacity={0.7}
            >
              <Ionicons name={"chatbubble-outline"} size={16} color={CYB.cyan} />
            </TouchableOpacity>
          </View>
        );
      })}
    </View>
  );

  const RewardsPanel = () => (
    <View style={s.panelWrap}>
      <Text style={s.panelTitle}>OPERATION REWARDS</Text>
      {[
        { icon: 'star-outline', label: 'XP Gagne', value: '+250 XP', color: CYB.cyan },
        { icon: 'person-outline', label: 'Reputation Nina', value: '+15', color: CYB.green },
        { icon: 'analytics-outline', label: 'Intel Points', value: '+3', color: CYB.purple },
        { icon: 'ribbon-outline', label: 'Badge', value: certLabel, color: CYB.orange },
      ].map(r => (
        <View key={r.label} style={s.rewardRow}>
          <Ionicons name={r.icon as any} size={15} color={r.color} />
          <Text style={s.rewardLabel}>{r.label}</Text>
          <Text style={[s.rewardValue, { color: r.color }]}>{r.value}</Text>
        </View>
      ))}
    </View>
  );

  const ObjectivesPanel = () => (
    <View style={s.panelWrap}>
      <Text style={s.panelTitle}>OBJECTIVES</Text>
      {chapter.objectives.map(obj => (
        <View key={obj.id} style={s.objRow}>
          <View style={[s.objIcon, { backgroundColor: obj.completed ? CYB.green + '33' : 'transparent', borderColor: obj.completed ? CYB.green : CYB.textMute }]}>
            {obj.completed && <Ionicons name={"checkmark"} size={12} color={CYB.green} />}
          </View>
          <Text style={[s.objText, obj.completed && s.objDone, { color: obj.completed ? CYB.textSec : CYB.textPrim }]}>
            {obj.title}
          </Text>
        </View>
      ))}
    </View>
  );

  const AbandonBtn = () => (
    <TouchableOpacity style={s.abandonBtn} onPress={() => navigation.goBack()} activeOpacity={0.8}>
      <Ionicons name={"exit-outline"} size={16} color={CYB.red} />
      <Text style={s.abandonText}>ABANDON OPERATION</Text>
    </TouchableOpacity>
  );

  const RightSidebar = () => (
    <View style={s.rightSidebar}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ProgressionPanel />
        <TeamPanel />
        <RewardsPanel />
        <ObjectivesPanel />
        <AbandonBtn />
        <View style={{ height: 80 }} />
      </ScrollView>
    </View>
  );

  const MobileTabBar = () => (
    <View style={s.mobileTabBar}>
      {(['timeline', 'team', 'progression'] as const).map(tab => (
        <TouchableOpacity
          key={tab}
          style={[s.mobileTabItem, mobileTab === tab && s.mobileTabItemActive]}
          onPress={() => setMobileTab(tab)}
          activeOpacity={0.7}
        >
          <Text style={[s.mobileTabText, mobileTab === tab && s.mobileTabTextActive]}>
            {tab === 'timeline' ? 'Timeline' : tab === 'team' ? 'Team' : 'Progression'}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  // Passerelle de déblocage : chargement / chapitre verrouillé.
  if (gate === 'loading') {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center' }]} edges={['top', 'left', 'right']}>
        <ActivityIndicator color={CYB.cyan} />
      </SafeAreaView>
    );
  }
  if (gate === 'locked') {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center', padding: 24 }]} edges={['top', 'left', 'right']}>
        <Ionicons name="lock-closed" size={48} color={CYB.textSec} />
        <Text style={{ color: CYB.textPrim, fontSize: 18, fontWeight: '700', marginTop: 16, textAlign: 'center' }}>Chapitre verrouillé</Text>
        <Text style={{ color: CYB.textSec, fontSize: 14, marginTop: 8, textAlign: 'center' }}>
          Terminez le chapitre précédent pour débloquer celui-ci.
        </Text>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ marginTop: 20, backgroundColor: CYB.cyan, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}>
          <Text style={{ color: '#001018', fontWeight: '700' }}>Retour aux chapitres</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={s.root} edges={['top', 'left', 'right']}>
      <View style={s.mainArea}>
        {isDesktop ? (
          <View style={s.desktopCols}>
            <View style={s.centreCol}>
              {activeItem ? (
                <View style={{ flex: 1 }}>
                  <TouchableOpacity onPress={() => setActiveItem(null)} style={s.inlineBackBtn}>
                    <Ionicons name={'arrow-back'} size={18} color={'#00f0ff'} />
                    <Text style={{ color: '#00f0ff', marginLeft: 6, fontSize: 13, fontWeight: '600' }}>Retour</Text>
                  </TouchableOpacity>
                  {activeItem.type === 'scene' && (
                    <SceneReaderScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'chat' && (
                    <MissionChatScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'task' && (
                    <TaskDetailScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: navigation.navigate } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'quiz' && (
                    <QuizScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: navigation.navigate } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'card' && (
                    <MissionCardsScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: navigation.navigate } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                </View>
              ) : (
                <ScrollView ref={timelineScrollRef} style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
                  <MissionHeader />
                  <View style={s.centreContent}>
                    <TimelineColumn />
                  </View>
                  <View style={{ height: 80 }} />
                </ScrollView>
              )}
            </View>
            <RightSidebar />
          </View>
        ) : (
          <View style={{ flex: 1 }}>
            <MissionHeader />
            <MobileTabBar />
            <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
              <View style={s.mobilePad}>
                {mobileTab === 'timeline' && (
                  <>
                    <TimelineColumn />
                    <View style={{ height: 12 }} />
                    <AbandonBtn />
                  </>
                )}
                {mobileTab === 'team' && (
                  <>
                    <TeamPanel />
                    <View style={{ height: 12 }} />
                    <RewardsPanel />
                  </>
                )}
                {mobileTab === 'progression' && (
                  <>
                    <ProgressionPanel />
                    <View style={{ height: 12 }} />
                    <ObjectivesPanel />
                  </>
                )}
                <View style={{ height: 80 }} />
              </View>
            </ScrollView>
          </View>
        )}
      </View>

      <Modal
        visible={!!chatCharacter}
        transparent
        animationType={"fade"}
        onRequestClose={() => setChatCharacter(null)}
      >
        <TouchableOpacity
          style={s.modalBackdrop}
          activeOpacity={1}
          onPress={() => setChatCharacter(null)}
        >
          <TouchableOpacity
            style={s.modalBox}
            activeOpacity={1}
            onPress={() => {}}
          >
            <View style={s.modalHeader}>
              <View style={s.modalCharInfo}>
                {chatCharacter?.avatar ? (
                  <Image source={chatCharacter.avatar} style={s.modalAvatar} resizeMode={"cover"} />
                ) : (
                  <View style={[s.modalAvatar, s.modalAvatarFb]}>
                    <Text style={s.modalAvatarInit}>{chatCharacter?.name?.[0] ?? '?'}</Text>
                  </View>
                )}
                <View>
                  <Text style={s.modalCharName}>{chatCharacter?.name}</Text>
                  <Text style={s.modalCharRole}>{chatCharacter?.role}</Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => setChatCharacter(null)} style={s.modalCloseBtn}>
                <Ionicons name={"close"} size={20} color={CYB.textSec} />
              </TouchableOpacity>
            </View>
            <FlatList
              style={s.messageList}
              data={chatMessages[chatCharacter?.id ?? ''] ?? []}
              keyExtractor={(_, i) => String(i)}
              renderItem={({ item: msg }) => (
                <View style={[s.messageBubble, msg.from === 'player' ? s.bubblePlayer : s.bubbleChar]}>
                  <Text style={[s.bubbleText, msg.from === 'player' ? s.bubbleTextPlayer : s.bubbleTextChar]}>
                    {msg.text}
                  </Text>
                </View>
              )}
              ListEmptyComponent={
                <Text style={s.emptyChat}>Aucun message. Commencez la conversation.</Text>
              }
            />
            <View style={s.chatInputRow}>
              <TextInput
                style={s.chatInput}
                value={chatInput}
                onChangeText={setChatInput}
                placeholder={"Envoyer un message..."}
                placeholderTextColor={CYB.textMute}
                onSubmitEditing={handleSendMessage}
              />
              <TouchableOpacity style={s.sendBtn} onPress={handleSendMessage} activeOpacity={0.8}>
                <Ionicons name={"send"} size={16} color={CYB.cyan} />
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: CYB.bg },
  mainArea: { flex: 1 },
  desktopCols: { flex: 1, flexDirection: 'row' },
  centreCol: { flex: 1 },
  centreContent: { paddingHorizontal: 24, paddingTop: 16 },
  rightSidebar: { width: 300, borderLeftWidth: 1, borderLeftColor: CYB.border },
  mobilePad: { paddingHorizontal: 14, paddingTop: 12 },
  threatRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  muteBtn: { padding: 4 },
  mobileTabBar: { flexDirection: 'row', backgroundColor: CYB.surface, borderBottomWidth: 1, borderBottomColor: CYB.border },
  mobileTabItem: { flex: 1, paddingVertical: 10, alignItems: 'center' },
  mobileTabItemActive: { borderBottomWidth: 2, borderBottomColor: CYB.cyan },
  mobileTabText: { fontSize: 12, fontWeight: '600', color: CYB.textMute, fontFamily: 'monospace' },
  mobileTabTextActive: { color: CYB.cyan },
  headerWrap: { height: 180, position: 'relative', overflow: 'hidden' },
  bannerImg: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' } as any,
  bannerOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(10,14,26,0.78)' },
  bannerContent: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: 20, justifyContent: 'flex-end' },
  backBtn: { position: 'absolute', top: 16, left: 16, width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(0,0,0,0.5)', borderWidth: 1, borderColor: CYB.border, alignItems: 'center', justifyContent: 'center' },
  bannerTitles: { gap: 4 },
  threatBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, backgroundColor: 'rgba(255,45,85,0.15)', borderWidth: 1, borderColor: CYB.red, borderRadius: 4 },
  threatText: { color: CYB.red, fontSize: 10, fontWeight: '700', fontFamily: 'monospace', letterSpacing: 1 },
  missionTitle: { color: '#ffffff', fontSize: 24, fontWeight: '900', fontFamily: 'monospace', letterSpacing: 2, textShadowColor: CYB.cyan, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 12 },
  missionSub: { color: CYB.textSec, fontSize: 12 },
  certRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  certBadge: { paddingHorizontal: 8, paddingVertical: 3, backgroundColor: 'rgba(0,240,255,0.12)', borderWidth: 1, borderColor: CYB.borderCy, borderRadius: 4 },
  certText: { color: CYB.cyan, fontSize: 10, fontWeight: '700', fontFamily: 'monospace' },
  stepCount: { color: CYB.textSec, fontSize: 11 },
  timelineWrap: { position: 'relative', paddingLeft: 44 },
  verticalLine: { position: 'absolute', left: 14, top: 12, bottom: 12, width: 2, backgroundColor: 'rgba(0,240,255,0.12)' },
  timelineRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14 },
  timelineLeft: { width: 44, position: 'absolute', left: -44, top: 14, alignItems: 'center', gap: 4 },
  timelineNum: { fontSize: 9, fontWeight: '800', color: CYB.textMute, fontFamily: 'monospace' },
  timelineDot: { width: 10, height: 10, borderRadius: 5, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.8, shadowRadius: 6, elevation: 3 },
  card: { flex: 1, backgroundColor: CYB.card, borderRadius: 12, borderWidth: 1, borderColor: CYB.border, padding: 14, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 2, overflow: 'hidden' },
  cardLocked: { opacity: 0.55 },
  cardTopRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  typeBadge: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 5 },
  typeBadgeText: { fontSize: 9, fontWeight: '800', fontFamily: 'monospace', letterSpacing: 0.5 },
  durationRow: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  durationText: { fontSize: 10, color: CYB.textMute },
  statusBadge: { marginLeft: 'auto' as any, flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 3, backgroundColor: 'rgba(0,240,255,0.08)', borderRadius: 5 },
  statusLocked: { backgroundColor: 'rgba(255,255,255,0.04)' },
  statusText: { fontSize: 9, fontWeight: '700', fontFamily: 'monospace' },
  cardTitle: { fontSize: 14, fontWeight: '700', color: CYB.textPrim, marginBottom: 5, lineHeight: 20 },
  cardDesc: { fontSize: 12, color: CYB.textSec, lineHeight: 17, marginBottom: 8 },
  cardMeta: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 },
  codeChip: { paddingHorizontal: 6, paddingVertical: 3, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 4 },
  codeText: { fontSize: 10, fontWeight: '600', color: CYB.textSec, fontFamily: 'monospace' },
  priorityChip: { paddingHorizontal: 6, paddingVertical: 3, borderRadius: 4 },
  priorityText: { fontSize: 10, fontWeight: '700' },
  avatarsRight: { marginLeft: 'auto' as any },
  charAvatarSm: { width: 30, height: 30, borderRadius: 15, marginLeft: 'auto' as any },
  lockOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 12, alignItems: 'center', justifyContent: 'center', gap: 6, padding: 16 },
  lockText: { color: CYB.textMute, fontSize: 11, textAlign: 'center' },
  actionBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', paddingHorizontal: 14, paddingVertical: 7, borderWidth: 1, borderRadius: 7 },
  actionBtnText: { fontSize: 12, fontWeight: '600' },
  panelWrap: { padding: 16, borderBottomWidth: 1, borderBottomColor: CYB.border },
  panelTitle: { fontSize: 10, fontWeight: '800', color: CYB.cyan, fontFamily: 'monospace', letterSpacing: 2, marginBottom: 12 },
  progSceneLabel: { fontSize: 12, color: CYB.textSec, marginBottom: 8 },
  progTrack: { height: 4, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden', marginBottom: 4 },
  progFill: { position: 'absolute', left: 0, top: 0, height: '100%', backgroundColor: CYB.cyan, borderRadius: 2 },
  progItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  progDot: { width: 7, height: 7, borderRadius: 4, flexShrink: 0 },
  progItemText: { fontSize: 11, flex: 1 },
  teamRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  teamAvatar: { width: 36, height: 36, borderRadius: 18, flexShrink: 0 },
  teamAvatarFb: { backgroundColor: 'rgba(0,240,255,0.12)', alignItems: 'center', justifyContent: 'center' },
  teamAvatarInit: { color: CYB.cyan, fontSize: 13, fontWeight: '700' },
  teamInfo: { flex: 1 },
  teamName: { fontSize: 12, fontWeight: '700', color: CYB.textPrim },
  teamRole: { fontSize: 10, color: CYB.textSec, marginBottom: 4 },
  relationRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  relationTrack: { flex: 1, height: 3, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' },
  relationFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 2 },
  relationScore: { fontSize: 10, fontFamily: 'monospace', flexShrink: 0 },
  chatIconBtn: { padding: 6, marginLeft: 4 },
  rewardRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  rewardLabel: { flex: 1, fontSize: 12, color: CYB.textSec },
  rewardValue: { fontSize: 12, fontWeight: '700', fontFamily: 'monospace' },
  objRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  objIcon: { width: 18, height: 18, borderRadius: 4, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  objText: { fontSize: 12, flex: 1, lineHeight: 17 },
  objDone: { textDecorationLine: 'line-through' },
  abandonBtn: { margin: 16, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1, borderColor: CYB.red, borderRadius: 8, backgroundColor: 'rgba(255,45,85,0.08)', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  abandonText: { color: CYB.red, fontSize: 12, fontWeight: '700', fontFamily: 'monospace', letterSpacing: 1 },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.8)', alignItems: 'center', justifyContent: 'center' },
  modalBox: { width: 420, maxWidth: '90%' as any, maxHeight: '80%' as any, backgroundColor: CYB.surface, borderWidth: 1, borderColor: CYB.borderCy, borderRadius: 12, overflow: 'hidden' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderBottomWidth: 1, borderBottomColor: CYB.border },
  modalCharInfo: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  modalAvatar: { width: 40, height: 40, borderRadius: 20 },
  modalAvatarFb: { backgroundColor: 'rgba(0,240,255,0.12)', alignItems: 'center', justifyContent: 'center' },
  modalAvatarInit: { color: CYB.cyan, fontSize: 16, fontWeight: '700' },
  modalCharName: { fontSize: 14, fontWeight: '700', color: CYB.textPrim },
  modalCharRole: { fontSize: 11, color: CYB.textSec },
  modalCloseBtn: { padding: 6 },
  messageList: { maxHeight: 300, padding: 12 },
  emptyChat: { color: CYB.textMute, fontSize: 12, textAlign: 'center', paddingVertical: 20 },
  messageBubble: { maxWidth: '75%' as any, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, marginBottom: 8 },
  bubblePlayer: { alignSelf: 'flex-end', backgroundColor: 'rgba(0,240,255,0.15)', borderWidth: 1, borderColor: CYB.borderCy },
  bubbleChar: { alignSelf: 'flex-start', backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: CYB.border },
  bubbleText: { fontSize: 13, lineHeight: 18 },
  bubbleTextPlayer: { color: CYB.cyan },
  bubbleTextChar: { color: CYB.textSec },
  chatInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: CYB.border },
  chatInput: { flex: 1, height: 38, paddingHorizontal: 12, backgroundColor: CYB.card, borderWidth: 1, borderColor: CYB.border, borderRadius: 8, color: CYB.textPrim, fontSize: 13 },
  sendBtn: { width: 38, height: 38, borderRadius: 8, backgroundColor: 'rgba(0,240,255,0.1)', borderWidth: 1, borderColor: CYB.borderCy, alignItems: 'center', justifyContent: 'center' },
  // Chapter accordion styles
  chapterAccordion: { marginBottom: 4 },
  chapterHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 4, borderRadius: 6 },
  chapterHeaderLocked: { opacity: 0.5 },
  chapterNum: { width: 22, height: 22, borderRadius: 5, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  chapterNumText: { fontSize: 9, fontWeight: '800', fontFamily: 'monospace' },
  chapterTitle: { fontSize: 11, fontWeight: '700', color: CYB.textPrim, marginBottom: 3 },
  chapterProgRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  chapterProgTrack: { flex: 1, height: 2, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 1, overflow: 'hidden' },
  chapterProgFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 1 },
  chapterProgPct: { fontSize: 9, fontFamily: 'monospace', flexShrink: 0 },
  chapterItems: { paddingLeft: 30, paddingBottom: 4, gap: 2 },
  inlineBackBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(0,240,255,0.15)', backgroundColor: 'rgba(0,240,255,0.04)' },
});
